import * as React from 'react'
import moment from 'moment'
import { TourImportCacheFileModel, TourImportCacheModel, TourImportModel } from './TourImportModel'
import {  DataTable, DataTableColumnModel, DataTableModel, defaultLabelSort, EntitiesPaginatedModel, FilterType, FilterValueType, LoadingSpinner, nameof, PaginationModel, PInTableList, Select, SelectOptionModel, Buttons } from 'traveldesk-ui'
import ExternalIntegrationsApiService, { ApiIntegrationOption } from '../../services/ExternalIntegrationsApiService'
import { useDispatch } from 'react-redux'
import { toHHMMString } from '../../utils/stringUtils'
const getStringValue = (item: Record<string, unknown>, keys: string[]): string => {
    for (const key of keys) {
        const value = item[key]
        if (typeof value === 'string' && value.trim()) {
            return value
        }
    }
    return ''
}
class ToursImportTableFilter {
    countryId?: string
    cityId?: string
    categoryId?: string
    page:number = 1
}
const getFilterStringValue = (filterValue: FilterValueType): string | undefined => {
    if (!filterValue || filterValue.value == null) {
        return undefined
    }
    if (typeof filterValue.value === 'string' || typeof filterValue.value === 'number') {
        return filterValue.value.toString()
    }
    return undefined
}
const getCountLabel = (count: number, label: string): string => count ? `${count} ${label}` : ''
const toSelectOptions = (data: unknown[] | undefined, idKeys: string[], labelKeys: string[]): SelectOptionModel[] => {
    if (!data) {
        return []
    }
    return data
        .map(item => {
            const source = item as Record<string, unknown>
            const value = getStringValue(source, idKeys)
            const label = getStringValue(source, labelKeys)
            return {
                value,
                label: label || value,
            } as SelectOptionModel
        })
        .filter(option => !!option.value)
        .sort(defaultLabelSort)
}
const ToursImportTablePage = () => {
    const dispatch = useDispatch()
    const [data, setData] = React.useState<EntitiesPaginatedModel<TourImportCacheModel>>(new EntitiesPaginatedModel<TourImportCacheModel>())
    const [expandedToursIds, setExpandedToursIds] = React.useState<Map<string, boolean>>(new Map<string,boolean>())
    const [filter, setFilter] = React.useState<ToursImportTableFilter>(new ToursImportTableFilter())
    const [countriesOptions, setCountriesOptions] = React.useState<SelectOptionModel[]>([])
    const [contriesHash, setCountriesHash] = React.useState<Map<string, string>>(new Map<string,string>())
    const [citiesOptions, setCitiesOptions] = React.useState<SelectOptionModel[]>([])
    const [citiesHash, setCitiesHash] = React.useState<Map<string, string>>(new Map<string,string>())
    const [categoriesOptions, setCategoriesOptions] = React.useState<SelectOptionModel[]>([])
    const [categoriesHash, setCategoriesHash] = React.useState<Map<string, string>>(new Map<string,string>())
    const [loading, setLoading] = React.useState(false)   
    const [apiIntegrationId, setApiIntegrationId] = React.useState<string>('')
    const [apiIntegrationsOptions, setApiIntegrationsOptions] = React.useState<ApiIntegrationOption[]>([])
    const requestApiIntegrations = async () => {
        const response = await ExternalIntegrationsApiService.getApiIntegrations(dispatch)
        if (response.IsOk && response.Content) {
            setApiIntegrationsOptions(response.Content)
            if(response.Content.length > 0 && response.Content.length == 1) {
                setApiIntegrationId(response.Content[0].value as string)
            }
        }
    }
    React.useEffect(() => {
        requestApiIntegrations()
    },[])
    const requestFiltersOptions = async () => {
        if (!apiIntegrationId) {
            setCountriesOptions([])
            setCountriesHash(new Map<string,string>())
            setCitiesOptions([])
            setCitiesHash(new Map<string,string>())
            setCategoriesOptions([])
            return
        }
        const [countriesResponse, citiesResponse, categoriesResponse] = await Promise.all([
            ExternalIntegrationsApiService.getExternalCountries(dispatch, apiIntegrationId),
            ExternalIntegrationsApiService.getExternalCities(dispatch, apiIntegrationId),
            ExternalIntegrationsApiService.getExternalCategories(dispatch, apiIntegrationId),
        ])
        if (countriesResponse.IsOk && countriesResponse.Content) {
            setCountriesOptions(countriesResponse.Content.sort(defaultLabelSort))
            setCountriesHash(countriesResponse.Content.reduce((acc, item) => {
                acc.set(item.value as string, item.label)
                return acc
            }, new Map<string,string>()))
        }
        if (citiesResponse.IsOk && citiesResponse.Content) {
            setCitiesOptions(
                toSelectOptions(citiesResponse.Content as unknown[], ['apiIntegrationCityId', 'ApiIntegrationCityId', 'id', 'value'], ['apiIntegrationCityName', 'ApiIntegrationCityName', 'name', 'label']),
            )
            setCitiesHash(citiesResponse.Content.reduce((acc, item) => {
                acc.set(item.apiIntegrationCityId, item.apiIntegrationCityName)
                return acc
            }, new Map<string,string>()))
        }
        if (categoriesResponse.IsOk && categoriesResponse.Content) {
            setCategoriesOptions(
                toSelectOptions(categoriesResponse.Content as unknown[], ['apiIntegrationCategoryId', 'ApiIntegrationCategoryId', 'id', 'value'], ['apiIntegrationCategoryName', 'ApiIntegrationCategoryName', 'name', 'label']),
            )
            setCategoriesHash(categoriesResponse.Content.reduce((acc, item) => {
                acc.set(item.apiIntegrationCategoryId, item.apiIntegrationCategoryName)
                return acc
            }, new Map<string,string>()))
        }
    }
    React.useEffect(() => {
        requestFiltersOptions()
    },[apiIntegrationId])
    const requestData = async () => {
        setExpandedToursIds(new Map<string, boolean>())
        if (!apiIntegrationId) {
            setData(new EntitiesPaginatedModel<TourImportCacheModel>())
            return
        }
        setLoading(true)
        const response = await ExternalIntegrationsApiService.getExternalToursPaginated(dispatch, {
            apiIntegrationId: Number(apiIntegrationId),
            page: filter.page,
            countryId: filter.countryId,
            cityId: filter.cityId,
            categoryId: filter.categoryId,
        })
        if (response.IsOk && response.Content) {
            const pagingInfo = Object.assign(new PaginationModel(), response.Content.pagingInfo)
            const toursCache = response.Content.entities.map(item => Object.assign(new TourImportCacheModel(), {
                apiProviderId: item.apiProviderId,
                externalId: item.externalId,
                fileName: item.fileName,
                isLoading: true,
            }))
            setData(Object.assign(new EntitiesPaginatedModel<TourImportCacheModel>(), {
                entities: toursCache,
                pagingInfo,
            }))
            setLoading(false)

            const loaded = await Promise.all(toursCache.map(async item => {
                if (!item.fileName) {
                    return Object.assign(new TourImportCacheModel(), item, { isLoading: false })
                }
                const cache = await ExternalIntegrationsApiService.getExternalTourCache(item.fileName)
                return Object.assign(new TourImportCacheModel(), item, cache || {}, {
                    fileName: item.fileName,
                    isLoading: false,
                })
            }))
            setData(Object.assign(new EntitiesPaginatedModel<TourImportCacheModel>(), {
                entities: loaded,
                pagingInfo,
            }))
            return
        }
        setLoading(false)
    }
    React.useEffect(() => {
        requestData()
    }, [apiIntegrationId, filter])
    const onApiIntegrationChange = (option: SelectOptionModel) => {
        setApiIntegrationId(option.value ? option.value.toString() : '')
        setFilter(new ToursImportTableFilter())
    }
    const onFilterValueChange = (fieldName: string, filterValue: FilterValueType) => {
        setFilter(Object.assign(new ToursImportTableFilter(), filter, {
            [fieldName]: getFilterStringValue(filterValue),
            page: 1,
        }))
    }
    const onPageChange = (page: number) => {
        setFilter(Object.assign(new ToursImportTableFilter(), filter, { page }))
    }
    const onClearFilter = () => {
        setFilter(new ToursImportTableFilter())
    }
    const toggleExpandTour = (tourId: string) => {
        const newMap = Object.assign(new Map<string, boolean>(), expandedToursIds)
        if(newMap.has(tourId)){
            newMap.set(tourId, !(newMap.get(tourId)))
        }
        else {
            newMap.set(tourId, true)
        }
        setExpandedToursIds(newMap)
    }
    const getOffers = (tour: TourImportModel) => tour.offers || []
    const initDataTable = () => {
        const columns = [            
            new DataTableColumnModel({
                displayName:"Country",
                width: 50,
                fieldName:nameof<TourImportModel>('externalCountryId'),
                filterFieldName:"countryId",
                filterType: FilterType.SELECT,
                filterOptions: countriesOptions,
                filterValue: filter.countryId,
                customRenderer:(obj: TourImportModel) => (obj as any).isLoading ? 'Loading...' : (obj as any).externalCountryId,
            }),
            new DataTableColumnModel({
                displayName:"City",
                width: 50,
                fieldName: nameof<TourImportModel>('externalCityId'),
                filterFieldName:"cityId",
                filterType: FilterType.SELECT,
                filterOptions: citiesOptions,
                filterValue: filter.cityId,
            }),
            new DataTableColumnModel({
                displayName: 'Tour Name',
                width: 200,
                fieldName: nameof<TourImportModel>('name'),
                filterType: FilterType.TEXT,
            }),
            new DataTableColumnModel({
                displayName:"Category",
                width: 150,
                fieldName:nameof<TourImportModel>('externalCategoryIds'),
                filterFieldName:"categoryId",
                filterType: FilterType.SELECT,
                filterOptions: categoriesOptions,
                filterValue: filter.categoryId,
                customRenderer: (tour: TourImportModel) => tour.externalCategoryIds?.join(', ') || '',
            }),
            new DataTableColumnModel({
                displayName:"Descr",
                width:200,
                fieldName:nameof<TourImportModel>('description'),                
            }),
            new DataTableColumnModel({
                displayName:"$",
                width:40,
                fieldName:nameof<TourImportModel>('externalCurrencyId'),                
            }),
            new DataTableColumnModel({
                displayName:"Offer Name",     
                width: 200,           
                fieldName:nameof<TourImportModel>('offers'),
                customRenderer: (tour: TourImportModel) => getOffers(tour)
                    .map(offer => offer.nameDisplay || offer.externalId)
                    .filter(Boolean)
                    .join(', '),
            }),                        
            new DataTableColumnModel({
                displayName:"Type",
                fieldName:"type",
                width:100,
                customRenderer: (tour: TourImportModel) => getOffers(tour)
                    .map(offer => offer.bookingType)
                    .filter(value => value != null)
                    .join(', '),
            }),
            new DataTableColumnModel({
                displayName:"Guests",
                fieldName:"",
                width:80,
                customRenderer: (tour: TourImportModel) => getOffers(tour)
                    .map(offer => `${offer.minNumOfPeople || 0}-${offer.maxNumOfPeople || 0}`)
                    .join(', '),
            }),
            new DataTableColumnModel({
                displayName:"Custom Inputs",
                fieldName:"customInputs",
                width:40,
                customRenderer: (tour: TourImportModel) =>
                    getCountLabel(getOffers(tour).reduce((res, offer) => res + (offer.customInputs?.length || 0), 0), 'inputs'),
            }),
            new DataTableColumnModel({
                displayName:"TimeSlots",
                fieldName:"timeSlots",
                width:80,
                customRenderer: (tour: TourImportModel) =>
                    getCountLabel(getOffers(tour).reduce((res, offer) => res + (offer.timeSlots?.length || 0), 0), 'slots'),
            }),
            new DataTableColumnModel({
                width:200,
                displayName:"Prices",
                customRenderer: (tour: TourImportModel) =>
                    getCountLabel(getOffers(tour).reduce((res, offer) => res + (offer.pricingPeriods?.length || 0), 0), 'prices'),
            })
        ]
        return new DataTableModel({
            columns,
            name: 'Tours Import',
            fileName: 'tours-import.xlsx',
            customToolBarComponents:[
                () => <div style={{backgroundColor: 'white'}}><Select placeholder='Select api integration' options={apiIntegrationsOptions} value={apiIntegrationId} onChange={onApiIntegrationChange} /></div>
            ],            
            customActions: [],
        })
    }
    const handleSyncTour = async (externalId: string) => {
        const reponse = await ExternalIntegrationsApiService.syncTour(dispatch, externalId)
    }
    const handleRefreshCache = async (externalId: string) => {
        const response = await ExternalIntegrationsApiService.updateTourCache(dispatch, apiIntegrationId, externalId)
        if(response.IsOk && response.Content) {
            setData(Object.assign(new EntitiesPaginatedModel<TourImportCacheModel>(), {
                entities: data.entities.map(item => Object.assign(new TourImportCacheModel(), item, { isLoading: true })),
                pagingInfo: data.pagingInfo,
            }))
            const cache = await ExternalIntegrationsApiService.getExternalTourCache(response.Content.fileName)
            if(cache!=null && cache.tourImportModel){
                setData(Object.assign(new EntitiesPaginatedModel<TourImportCacheModel>(), {
                    entities: data.entities.map(item => Object.assign(new TourImportCacheModel(), item, {
                        fileName: item.fileName,     
                        cacheDateTime: cache.cacheDateTime,                   
                        tourImportModel: item.externalId == externalId ? TourImportModel.Create(cache.tourImportModel as TourImportModel) : item.tourImportModel,
                        isLoading: false,
                    })),
                    pagingInfo: data.pagingInfo,
                }))
            }
        } else {
            alert("Couldn't refresh cache: " + response.ErrorMessage)
        }
    }
    const customBody = () => {
        return <tbody className='test'>
            {data.entities.map(item => {
                const maxOffers = item.tourImportModel?.offers?.length??0>3 ? Math.min(3, item.tourImportModel?.offers?.length??0) : item.tourImportModel?.offers?.length??0                
                const showExpand = ((item.tourImportModel?.offers?.length??0)>3) && !(expandedToursIds.get(item.externalId)??false)
                const numOffersToShow = showExpand ? maxOffers : item.tourImportModel?.offers?.length??0
                const showCollapse = (expandedToursIds.get(item.externalId)??false)
                const offersRowSpan = (item.tourImportModel ? showCollapse ? item.tourImportModel.rowSpan : item.tourImportModel.rowSpanCollapse: 1)

                return <><tr key={item.externalId}>
                    <td style={{width:50, borderBottom:"2px solid black"}} rowSpan={offersRowSpan}>{item.tourImportModel?.externalCountryId ? contriesHash.get(item.tourImportModel.externalCountryId) : 'NOT LOADED'}</td>
                    <td style={{width:50, borderBottom:"2px solid black"}} rowSpan={offersRowSpan}>
                        {item.tourImportModel?.cityId
                        ? <span style={{backgroundColor:"light-green"}}>Mapped {item.tourImportModel.cityId}</span>
                        : <span style={{backgroundColor:"orange"}}>
                            {item.tourImportModel?.externalCityId ? citiesHash.get(item.tourImportModel.externalCityId) : 'NOT LOADED'}
                        </span>}                        
                    </td>
                    <td style={{width:200, borderBottom:"2px solid black"}} rowSpan={offersRowSpan}>
                        {item.tourImportModel 
                            ? <>{item.tourImportModel.name}<br/><span style={{fontSize:10, color: 'gray'}}>{item.externalId}</span></> 
                            : <><span style={{fontSize:12, color: 'gray'}}>{item.tourName}</span><br/><span style={{fontSize:10, color: 'gray'}}>{item.externalId}</span></>}
                        <div>
                            {item.mappedTourId && <a href={`/tours/${item.mappedTourId}`} target='_blank'><i style={{padding:5}} className='fa fa-eye '/></a>}
                            <i style={{padding:5, color: 'blue', cursor: 'pointer'}} title={`Refresh cache${item.cacheDateTime ? ` (${moment(item.cacheDateTime).format('DD/MM/YYYY HH:mm:ss')})` : ''}`} className='fa fa-refresh' onClick={() => handleRefreshCache(item.externalId)}/>
                            <i style={{padding:5, color: 'blue', cursor: 'pointer'}} title={item.mappedTourId ?"Update tour":"Import"} className='fa fa-cloud-download' onClick={() => handleSyncTour(item.externalId)}/>
                        </div>
                    </td>                    
                    {item.tourImportModel
                    ? <>
                        <td style={{width:150, borderBottom:"2px solid black"}} rowSpan={offersRowSpan}>{item.tourImportModel?.externalCategoryIds?.map(x=><PInTableList>{categoriesHash.get(x) ?? x}</PInTableList>) ?? <></>}</td>
                        <td style={{width:200, borderBottom:"2px solid black"}} rowSpan={offersRowSpan}>{item.tourImportModel?.description}</td>
                        <td style={{width:40, borderBottom:"2px solid black"}} rowSpan={offersRowSpan}>{item.tourImportModel?.externalCurrencyId}</td>
                        <td style={{width:200}} rowSpan={item.tourImportModel.firstOfferRowSpan}>{item.tourImportModel.firstOfferName}</td>                  
                        <td style={{width:100}} rowSpan={item.tourImportModel.firstOfferRowSpan}>{item.tourImportModel.firstOffer?.bookingType}</td>      
                        <td style={{width:80}} rowSpan={item.tourImportModel.firstOfferRowSpan}>{item.tourImportModel.firstOffer?.minNumOfPeople}-{item.tourImportModel.firstOffer?.maxNumOfPeople}</td>      
                        <td style={{width:40}} rowSpan={item.tourImportModel.firstOfferRowSpan}>{item.tourImportModel.firstOffer?.customInputs?.length}</td>      
                        <td style={{width:80}} rowSpan={item.tourImportModel.firstOfferRowSpan}>{item.tourImportModel.firstOffer?.timeSlots?.map(timeSlot => <PInTableList>{toHHMMString(timeSlot.startTime ?? 0)}-  {toHHMMString(timeSlot.endTime ?? 0)}</PInTableList>)}</td>
                        <td style={{width:200}}>{item.tourImportModel.firstOffer?.pricingPeriods?.length}</td>
                    </>
                    : item.isLoading ? 
                    <>                    
                        <td style={{width:1350,textAlign: 'center'}} colSpan={9}>Loading... <i className='fa fa-spinner fa-spin'/></td> 
                    </> 
                    : <td style={{width:1350,textAlign: 'center', backgroundColor: 'red'}} colSpan={9}>Error</td>}                    
                </tr>
                {item.tourImportModel && item.tourImportModel.offers?.length && item.tourImportModel.offers.length > 0 && item.tourImportModel.offers.slice(1,numOffersToShow).map((offer, index) => <tr key={offer.externalId}>
                    <td style={{width:200}}>
                        {offer.nameDisplay || offer.externalId}
                        <br/>
                         <span style={{fontSize:10, color: 'gray'}}>{offer.externalId}</span>
                        {showExpand && index >= (numOffersToShow - 2) && <Buttons.ButtonSmallGreen><i className='fa fa-chevron-down' onClick={() => toggleExpandTour(item.externalId)} /> Show +{(item.tourImportModel?.offers?.length??0) - maxOffers} offers</Buttons.ButtonSmallGreen>}
                        {showCollapse && index >= (numOffersToShow - 2)&& <Buttons.ButtonSmallGreen><i className='fa fa-chevron-up' onClick={() => toggleExpandTour(item.externalId)} /></Buttons.ButtonSmallGreen>}
                    </td>           
                    <td style={{width:100}}>{offer.bookingType}</td>      
                    <td style={{width:80}}>{offer.minNumOfPeople}-{offer.maxNumOfPeople}</td>      
                    <td style={{width:40}}>{offer.customInputs?.length}</td>      
                    <td style={{width:80}}>{offer.timeSlots?.map(timeSlot => <PInTableList>{toHHMMString(timeSlot.startTime ?? 0)} - {toHHMMString(timeSlot.endTime ?? 0)}</PInTableList>)}</td>
                    <td style={{width:200}}>{offer.pricingPeriods?.length}</td>
                </tr>)}
                </>
            })}
        </tbody>
    }
    return  (
        <DataTable                        
            data={[]}
            pagingInfo={data.pagingInfo}
            dataTable={initDataTable()}
            customBody={customBody()}
            onFilterValueChange={onFilterValueChange}
            onPageChange={onPageChange}
            onClearFilter={onClearFilter}
            isFullScreenable
            isFullScreen={true}
        />
    )
}
export default ToursImportTablePage